import { useState } from "react";
import Select, { createFilter } from "react-select";
import { Icons } from "../../../icons";
import { SelectOption } from "../constants/signup.types";

interface TypeAheadImportProps {
  inputId?: string;
  options: SelectOption[];
  onChange: (option: any) => void;
  minimumInputLength?: number;
  progressOnSelect: () => void;
  placeHolder?: string;
  searchPrefixOnly?: boolean;
  quickSelectOption?: SelectOption;
}

const TypeAhead = ({
  inputId,
  options,
  onChange,
  minimumInputLength = 0,
  placeHolder = "",
  searchPrefixOnly = false,
  quickSelectOption,
  progressOnSelect,
}: TypeAheadImportProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [selectedValue, setSelectedValue] = useState<SelectOption>(null);
  const [isFocused, setIsFocused] = useState(false);

  const filterOption = searchPrefixOnly
    ? createFilter({ matchFrom: "start" })
    : undefined;

  const handleSearchInputChange = (newValue: string) => {
    setSearchInput(newValue);
  };

  const handleOptionSelect = (option: SelectOption | null) => {
    onChange(option?.value || "");
    setSelectedValue(option);

    if (option) {
      progressOnSelect();
    }
  };

  // Removes default icons for a cleaner look
  // Passing in component to Select follows react-select best practices
  const EmptyIndicatorsContainer = () => {
    return null;
  };

  const meetsMinimumCharacterRequirement =
    searchInput.length >= minimumInputLength;
  const hasNoSelection = selectedValue === null;
  const shouldShowMenu =
    isFocused && meetsMinimumCharacterRequirement && hasNoSelection;

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-relative">
      <Select
        inputId={inputId}
        options={options}
        components={{ IndicatorsContainer: EmptyIndicatorsContainer }}
        inputValue={searchInput}
        onChange={handleOptionSelect}
        onInputChange={handleSearchInputChange}
        value={selectedValue}
        filterOption={filterOption}
        menuIsOpen={shouldShowMenu}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeHolder}
        styles={{
          // eslint-disable-next-line
          control: (baseStyles, _) => ({
            ...baseStyles,
            height: "64px",
            fontSize: "18px",
            backgroundColor: baseStyles.backgroundColor,
          }),
        }}
      />
      {quickSelectOption && (
        <button
          className="tw-mt-3 tw-text-sm"
          key={quickSelectOption.id}
          aria-label={`Select ${quickSelectOption.label}`}
          onClick={() => {
            handleOptionSelect(quickSelectOption);
          }}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              handleOptionSelect(quickSelectOption);
            }
          }}
        >
          <div className="tw-flex tw-items-center tw-gap-1 tw-italic">
            <span
              className="tw-font-medium tw-text-gray-500"
              style={{ fontSize: "12px" }}
            >
              {quickSelectOption.label}
            </span>
            <Icons.Forward className="tw-w-4 tw-h-4 tw-text-gray-500" />
          </div>
        </button>
      )}
    </div>
  );
};

export default TypeAhead;
